import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Form, Formik } from 'formik'
import { Dispatch } from 'redux'
import { RootAction } from 'typesafe-actions'

import { Modal } from '@driftt/tide-modal'

import useFilterSets from '../../hooks/useFilterSets'
import useFormValues from '../../hooks/useFormValues'
import useIntegrations from '../../hooks/useIntegrations'
import useSubscriptions from '../../hooks/useSubscriptionsBySections'
import withToast from '../../hooks/withToast'
import { getCurrentUser, getUserProperties } from '../../selectors'
import { User } from '../../typings/user'
import {
  getFormattedSubscriptions,
  getPropertyOperations,
  getUnsavedChanges,
  updateIntegrationNotifications,
  updatePropertiesNotifications,
  updateSavedFilterNotifications,
  updateSubscriptionNotifications
} from '../../utils/formik'
import NotificationsDefaultSettings from '../NotificationsDefaultSettings'
import NotificationsSavedFilterSettings from '../NotificationsSavedFilterSettings'

import styles from './styles.module.css'

const NotificationSettingsModal = withToast((props: any) => {
  const { toast, onClose, initialValues, onUpdateSettings } = props
  const [isRootModal, setIsRootModal] = useState(!props.savedFilterIdParam)

  const onSelectFilter = (filter: any) => {
    props.setSelectedAccountFilter(filter)
    setIsRootModal(!filter?.id)
  }

  const onSubmit = async (values: any, actions: any) => {
    actions.setSubmitting(true)
    await onUpdateSettings(values)
    actions.setSubmitting(false)
    toast && toast('Notification settings updated')
    onClose()
  }

  return (
    <Modal open={true} size="large" onClose={onClose} className={styles.modal}>
      <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={onSubmit}>
        {({ isSubmitting, values: formikValues, setFieldValue }) => (
          <Form translate={undefined} onAuxClick={undefined} onAuxClickCapture={undefined}>
            <>
              <NotificationsDefaultSettings
                hidden={!isRootModal}
                formikValues={formikValues}
                onSelectAccountFilter={onSelectFilter}
                numUnsavedChanges={getUnsavedChanges(initialValues, formikValues)}
                isSubmitting={isSubmitting}
                setFieldValue={setFieldValue}
              />
              <NotificationsSavedFilterSettings
                hidden={isRootModal}
                formikValues={formikValues}
                selectedFilter={props.selectedAccountFilter}
                onBack={() => onSelectFilter(undefined)}
                numUnsavedChanges={getUnsavedChanges(initialValues, formikValues)}
                isSubmitting={isSubmitting}
              />
            </>
          </Form>
        )}
      </Formik>
    </Modal>
  )
})

const NotificationsContainer = (props: any) => {
  const { savedFilterIdParam, onClose } = props
  const currentProperties = useSelector(getUserProperties)
  const currentUser = useSelector<any, User | undefined>(getCurrentUser)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const currentUserId = currentUser?.id || 0

  const { integrations } = useIntegrations()
  const { subscriptionValues: initialSubscriptions } = useSubscriptions()
  const { savedFilters, selectedAccountFilter, setSelectedAccountFilter } = useFilterSets(
    savedFilterIdParam
  )

  const initialValues = useFormValues(integrations, savedFilters, initialSubscriptions)

  const handleUpdateSettings = async (settings: any) => {
    const initialProperties = initialValues.properties

    const { properties, subscriptions, integrationSettings, savedFilters } = settings
    const { values: integrationValues } = integrationSettings

    const propertyOperations = getPropertyOperations(properties, initialProperties)
    const subscriptionPayload = getFormattedSubscriptions(subscriptions, initialSubscriptions)

    dispatch({
      type: 'RECEIVE_USER_SETTINGS',
      settings: { ...currentProperties, ...properties }
    })

    await updateSubscriptionNotifications(subscriptionPayload)
    await updateSavedFilterNotifications(currentUserId, savedFilters)
    await updateIntegrationNotifications(integrationSettings, integrationValues)
    await updatePropertiesNotifications(currentUserId, propertyOperations)
  }

  return (
    <NotificationSettingsModal
      onClose={() => onClose && onClose()}
      initialValues={initialValues}
      savedFilterIdParam={savedFilterIdParam}
      selectedAccountFilter={selectedAccountFilter}
      setSelectedAccountFilter={setSelectedAccountFilter}
      onUpdateSettings={handleUpdateSettings}
    />
  )
}

export default NotificationsContainer
